// Lube-specific helpers built on the shared API client. Keeps the
// component code decoupled from URL details.

import { api } from './api.js';
import { generateUuidV7 } from '@edv-next/contracts/mutation';

export interface LubePoint {
  id: string;
  site_id: string;
  organization_id: string;
  asset_id: string;
  lubricant_id: string | null;
  name: string;
  point_type: string;
  data: Record<string, unknown>;
  version: number;
  archived_at: string | null;
  created_at?: string;
}
export interface LubeRoute {
  id: string;
  site_id: string;
  organization_id: string;
  name: string;
  version: number;
  stops?: Array<{ seq: number; point_id: string }>;
}
export interface LubeTool {
  id: string;
  name: string;
  data: Record<string, unknown>;
}
export interface Lubricant {
  id: string;
  name: string;
  kind: string;
  specs: Record<string, unknown>;
  version: number;
}

export interface LubeImportPreview {
  site_id: string;
  accepted: Array<{ row_index: number; asset_id: string; point_payload: Record<string, unknown> }>;
  rejected: Array<{ row_index: number; reasons: string[] }>;
  conflicts: Array<{
    row_index: number;
    asset_id: string;
    incoming_name: string;
    existing_point_id: string;
    existing_version: number;
    point_payload: Record<string, unknown>;
  }>;
  as_of: string;
}

export const lubeApi = {
  async listPoints(siteId: string, opts: { cursor?: string; limit?: number } = {}) {
    const qs = new URLSearchParams();
    if (opts.cursor) qs.set('cursor', opts.cursor);
    if (opts.limit) qs.set('limit', String(opts.limit));
    const q = qs.toString();
    return api.json<{ data: LubePoint[]; cursor: string | null; as_of: string }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/lube/points${q ? '?' + q : ''}`,
      { method: 'GET' },
    );
  },
  async getPoint(id: string) {
    return api.json<LubePoint>(`/api/v1/lube/points/${encodeURIComponent(id)}`, { method: 'GET' });
  },
  async createPoint(siteId: string, deviceId: string, payload: Record<string, unknown>) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/sites/${encodeURIComponent(siteId)}/lube/points`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        expected_version: null,
        payload,
      }),
    });
  },
  async patchPoint(
    id: string,
    deviceId: string,
    expected_version: number,
    payload: Record<string, unknown>,
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/lube/points/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({ mutation_id: mid, device_id: deviceId, expected_version, payload }),
    });
  },
  async archivePoint(id: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/lube/points/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        expected_version,
        payload: {},
      }),
    });
  },
  async restorePoint(id: string, deviceId: string, expected_version: number) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/lube/points/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        expected_version,
        payload: {},
      }),
    });
  },
  async listRoutes(siteId: string) {
    return api.json<{ data: LubeRoute[] }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/lube/routes`,
      { method: 'GET' },
    );
  },
  async createRoute(siteId: string, deviceId: string, name: string) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/sites/${encodeURIComponent(siteId)}/lube/routes`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        expected_version: null,
        payload: { name },
      }),
    });
  },
  async setRouteStops(
    routeId: string,
    deviceId: string,
    expectedVersion: number,
    pointIds: string[],
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/lube/routes/${encodeURIComponent(routeId)}/stops`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        entity_id: routeId,
        expected_version: expectedVersion,
        payload: { stops: pointIds.map((point_id) => ({ point_id })) },
      }),
    });
  },
  async recordEvent(
    deviceId: string,
    pointId: string,
    eventKind: 'planned' | 'done' | 'skipped' | 'exception' = 'done',
    data: Record<string, unknown> = {},
  ) {
    const mid = generateUuidV7();
    return api.json(`/api/v1/lube/events`, {
      method: 'POST',
      headers: { 'Idempotency-Key': mid },
      body: JSON.stringify({
        mutation_id: mid,
        device_id: deviceId,
        payload: { point_id: pointId, event_kind: eventKind, data },
      }),
    });
  },
  async calcPreview(
    algorithm_key: string,
    inputs: Record<string, unknown>,
    plant_overrides?: Record<string, unknown>,
    lube_catalog?: unknown[],
  ) {
    return api.json(`/api/v1/lube/calc/preview`, {
      method: 'POST',
      body: JSON.stringify({ algorithm_key, inputs, plant_overrides, lube_catalog }),
    });
  },
  async previewImport(siteId: string, rows: Array<Record<string, unknown>>) {
    return api.json<LubeImportPreview>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/lube/import/preview`,
      {
        method: 'POST',
        body: JSON.stringify({ site_id: siteId, rows }),
      },
    );
  },
  async applyImport(
    siteId: string,
    deviceId: string,
    preview: LubeImportPreview,
    conflictMode: 'overwrite' | 'skip',
  ) {
    const mid = generateUuidV7();
    return api.json<{ added: number; replaced: number; skipped: number; mutation_ids: string[] }>(
      `/api/v1/sites/${encodeURIComponent(siteId)}/lube/import/apply`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': mid },
        body: JSON.stringify({
          site_id: siteId,
          mutation_id: mid,
          device_id: deviceId,
          conflict_mode: conflictMode,
          accepted: preview.accepted,
          conflicts: preview.conflicts,
        }),
      },
    );
  },
};
